import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FocusTrap } from "focus-trap-react";
import { XCircleIcon } from "@heroicons/react/24/outline";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: "default" | "half" | "full";
  width?: string;
  zIndex?: number; // for stacking
}

const bottomSheetHeightClasses = {
  default: "h-2/3",
  half: "h-1/2",
  full: "h-full",
};

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  height = "default",
  zIndex = 50,
  width,
}) => {
  const heightClass = bottomSheetHeightClasses[height];
  const widthClass = width ? width : "w-full";
  const sheetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  const portalTarget =
    typeof document !== "undefined"
      ? document.getElementById("bottom-sheet-root")
      : null;

  if (!portalTarget) return null;

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { y: number } },
  ) => {
    const threshold = 100;
    if (info.offset.y > threshold) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="backdrop"
          className={`fixed inset-0 flex items-end bg-black bg-opacity-30`}
          style={{ zIndex }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <FocusTrap
            focusTrapOptions={{
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              fallbackFocus: () => sheetRef.current!,
              clickOutsideDeactivates: true,
              escapeDeactivates: true,
            }}
          >
            <motion.div
              ref={sheetRef}
              className={`bottom-sheet-container relative bg-white rounded-t-2xl flex flex-col pb-8 ${heightClass} ${widthClass}`}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              drag="y"
              dragDirectionLock
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={handleDragEnd}
              style={{ touchAction: "none" }}
            >
              {/* Drag handle */}
              <div className="flex-shrink-0 pt-3 pb-2 cursor-grab active:cursor-grabbing">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
              </div>

              {/* Close button */}
              <motion.button
                onClick={onClose}
                className="bottom-sheet-close-button absolute right-4 top-4 z-10 text-gray-500 hover:text-black focus:outline-none"
                aria-label="Close Modal"
                whileTap={{ scale: 0.95 }}
              >
                <XCircleIcon className="bottom-sheet-close-icon h-10 w-10 text-gray-600" />
              </motion.button>

              {/* Scrollable content area */}
              <div
                ref={contentRef}
                className="bottom-sheet-body flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 pb-16"
                style={{
                  touchAction: "pan-y",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                }}
              >
                {children}
              </div>
            </motion.div>
          </FocusTrap>
        </motion.div>
      )}
    </AnimatePresence>,
    portalTarget,
  );
};

export default BottomSheet;
